import steps from '../components/Valuation/ValuationSteps';
import { IValuationContact, IValuationData, IValuationWidgetProps } from '../components/Valuation/ValuationWidget';
import JsonApiRequest, { IValuation } from './JsonApiRequest';
import PropApiRequest from './PropApiRequest';
import RequestResponse from './RequestResponse';

const valuationApiUrl = '/prop-api/valuation';

export interface IValuationRequestAdd {
    id: string;
}

type IValuationRequestAddSubmit = IValuationData & {
    id?: string;
    dossierUrl?: string;
    valueRange?: { lower: number; upper: number };
};

export type IValuationMatchData = {
    matches: string;
};

export type IValuationAffiliateData = {
    logo: { url: string; mimetype: string };
    name: string;
};

class ValuationRequest {
    public add = (data: IValuationRequestAddSubmit, variant?: IValuationWidgetProps['variant']): Promise<IValuationRequestAdd> => {
        return PropApiRequest.postRequest(valuationApiUrl + '/add?_format=json', { ...data, variant })
            .then((res) => {
                return res.getData() as IValuationRequestAdd;
            })
            .catch((e) => {
                console.error(e);
                return Promise.reject(e);
            });
    };

    public get = (uuid: string): Promise<IValuationData> => {
        return JsonApiRequest.getRequest(`/valuation_request/valuation_request/${uuid}`)
            .then((res) => {
                try {
                    const resData = res.getData();
                    const objData = JsonApiRequest.data2Object<IValuation>(resData).find((d) => d);
                    return this.valuation2data(objData);
                } catch (e) {
                    console.error(e);
                    return Promise.reject(e);
                }
            })
            .catch((e) => {
                console.error('Error on fetching propertyDetails', e);
                return Promise.reject(e);
            });
    };

    /**
     * @param uuid user uuid
     */
    public getByUser = (uuid: string): Promise<IValuationData[]> => {
        return JsonApiRequest.getRequest(`/valuation_request/valuation_request?filter[user_id.id][value]=${uuid}`)
            .then((res) => {
                try {
                    const resData = res.getData();
                    const objData = JsonApiRequest.data2Object<IValuation>(resData);
                    return objData.sort((a, b) => a.attributes.created.localeCompare(b.attributes.created)).map((d) => this.valuation2data(d));
                } catch (e) {
                    console.error(e);
                    return Promise.reject(e);
                }
            })
            .catch((e) => {
                console.error('Error on fetching ValuationPropertyDetails', e);
                return Promise.reject(e);
            });
    };

    public userHasValuation = (uuid: string): Promise<boolean> =>  {
        return JsonApiRequest.getRequest(`/valuation_request/valuation_request?filter[user_id.id][value]=${uuid}`).then((res)=> {
            return Promise.resolve(res.getData()?.data?.length ? true : false)
        }).catch((e) => {
            return Promise.resolve(false)
        })
    }

    public delete = (uuid: string): Promise<RequestResponse> => {
        return JsonApiRequest.deleteItem(`/jsonapi/valuation_request/valuation_request/${uuid}`);
    };

    public getMatches = (uuid: string): Promise<IValuationMatchData> => {
        return PropApiRequest.getRequest(`${valuationApiUrl}/${uuid}/matches`)
            .then((res) => {
                return res.getData();
            })
            .catch((e) => {
                console.error(e);
                return Promise.reject(e);
            });
    };

    public getAffiliate = (uuid: string): Promise<IValuationAffiliateData> => {
        return PropApiRequest.getRequest(`${valuationApiUrl}/${uuid}/affiliate`)
            .then((res) => {
                return res.getData();
            })
            .catch((e) => {
                console.error(e);
                return Promise.reject(e);
            });
    };

    /**
     * Dashboard: Profi-Hilfe vom Makler erhalten
     *
     * @param uuid valuation uuid
     * @param userId drupal id of user
     */
    public contactProvider = (uuid: string, userId: number, contactData: IValuationContact) => {
        const data = { id: uuid, user_id: userId, ...contactData };
        return PropApiRequest.postRequest(valuationApiUrl + '/contact-provider?_format=json', data)
            .then((res) => {
                return res.getData();
            })
            .catch((e) => {
                console.error(e);
                return Promise.reject(e);
            });
    };

    public code2text = (code: IValuationData['code']) => {
        return code === 'apartment' ? 'Wohnung' : code === 'house' ? 'Haus' : '';
    };

    public subcode2text = (code: IValuationData['code'], subcode: IValuationData['subcode']) => {
        return steps.find((s) => s.name === 'subcode').options[code][subcode] || 'Immobilie';
    };

    public valuation2data = (valuation: IValuation): IValuationData => {
        // TODO: replace rawData with new BE fields
        const rawData: IValuationData = valuation?.raw_data ? JSON.parse(valuation.raw_data) : null;
        const data: IValuationData = {
            id: valuation.id,

            buildingYear: Number(valuation.baujahr),
            city: valuation.address.locality,
            code: rawData.code,
            condition: valuation.zustand,
            landArea: Number(valuation.plot_area),
            livingArea: Number(valuation.living_area),
            numberOfRooms: Number(valuation.chamber_count),
            numberOfBathrooms: Number(valuation.bathroom_count),
            postCode: valuation.address.postal_code,
            // reason: undefined, // IReason;
            street: valuation.address.address_line1,
            subcode: rawData.subcode,
            // dealType: IDealType;
            hasGarage: rawData.hasGarage,
            hasParkingSpace: rawData.hasParkingSpace,
            dossier_id: valuation.dossier_id,
            dossier_url: valuation.dossier_url,
            dossier_url_valid_until: valuation.dossier_url_valid_until,
        };
        return data;
    };

    public patch = (uuid, data) => {
        return PropApiRequest.patchRequest(`${valuationApiUrl}/${uuid}`, data);
    };
}

export default new ValuationRequest();
